import React, { useState } from 'react';
import { useApp } from '../../context/SupabaseAppContext';

interface FirstTimeSetupProps {
  userEmail: string;
}

const FirstTimeSetup: React.FC<FirstTimeSetupProps> = ({ userEmail }) => {
  const { actions } = useApp();
  const [name, setName] = useState('');
  const [avatar, setAvatar] = useState('👤');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      await actions.updateProfile(name.trim(), avatar);
      // The context will reload data and the user will be properly set up
    } catch (error: any) {
      setError(error.message || 'Failed to complete setup');
      setLoading(false);
    }
  };

  const emojiOptions = ['👤', '😊', '🚀', '💡', '🔥', '⭐', '🎯', '💼', '🎨', '🔧', '📱', '💻'];

  return (
    <div className="min-h-screen bg-white flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          <div className="mx-auto mb-6">
            <img 
              src="/logo.jpg" 
              alt="Idea Hopper Logo" 
              className="h-[120px] w-auto object-contain mx-auto"
            />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            Welcome to Idea Hopper!
          </h2>
          <p className="text-lg text-gray-700">
            Let's complete your profile to get started
          </p>
          <p className="text-sm text-gray-500 mt-2">
            Signed in as: {userEmail}
          </p>
        </div>

        <div className="bg-white rounded-xl shadow-2xl p-8" style={{ boxShadow: '0 -8px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)' }}>
          <form className="space-y-6" onSubmit={handleSubmit}>
            <div>
              <label htmlFor="name" className="block text-sm font-medium text-gray-700">
                Full Name *
              </label>
              <input
                id="name"
                name="name"
                type="text"
                required
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-deep-teal focus:border-deep-teal"
                placeholder="Enter your full name"
                autoFocus
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">
                Choose Your Avatar
              </label>
              <div className="space-y-3">
                <div className="flex items-center space-x-3">
                  <span className="text-3xl">{avatar}</span>
                  <span className="text-sm text-gray-600">Selected avatar</span>
                </div>
                <div className="grid grid-cols-6 gap-2">
                  {emojiOptions.map((emoji) => (
                    <button
                      key={emoji}
                      type="button"
                      onClick={() => setAvatar(emoji)}
                      className={`p-2 text-2xl rounded-lg border-2 transition-colors ${
                        avatar === emoji 
                          ? 'border-deep-teal bg-deep-teal bg-opacity-10' 
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      {emoji}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {error && (
              <div className="text-center p-3 bg-red-50 border border-red-200 rounded-md">
                <p className="text-sm text-red-700">{error}</p>
              </div>
            )}

            <div>
              <button
                type="submit"
                disabled={loading || !name.trim()}
                className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-deep-teal hover:bg-dark-emerald focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-deep-teal disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                ) : (
                  'Complete Setup'
                )}
              </button>
            </div>
          </form>

          <div className="mt-6 pt-6 border-t border-gray-200">
            <div className="text-xs text-gray-500 text-center">
              <p>🎉 You're almost ready to start managing ideas!</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FirstTimeSetup; 